import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { API } from "@/lib/api";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in every field.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      if (res.data?.email_sent) {
        setSent(true);
        toast.success("Message sent — we'll get back to you soon.");
        setName("");
        setEmail("");
        setMessage("");
      } else {
        toast.error("We couldn't deliver your message right now. Please email us directly at support@vantstride.com.");
      }
    } catch (err) {
      toast.error("Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">Contact</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#0A2540] font-medium mt-3">
          Say hello.
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Questions about a Career Hub, feedback on a resource, or interested in partnerships? Send us a note.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-10 rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
        data-testid="contact-form"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-slate-600" htmlFor="contact-name">Name</label>
            <input
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="contact-name"
              className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600" htmlFor="contact-email">Email</label>
            <input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="contact-email"
              className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white"
              placeholder="you@school.edu"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs font-medium text-slate-600" htmlFor="contact-message">Message</label>
          <textarea
            id="contact-message"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            data-testid="contact-message"
            className="mt-1 w-full rounded-2xl border border-slate-200 focus:border-[#0A2540] focus:outline-none px-4 py-3 text-sm bg-white resize-none"
            placeholder="How can we help?"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="mt-6 inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#051A2E] transition-colors active:scale-95 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" /> Send message
            </>
          )}
        </button>
        {sent && (
          <p className="mt-4 text-sm text-emerald-700" data-testid="contact-sent">
            Thanks — we received your message and will reply within 2 business days.
          </p>
        )}
      </form>
    </div>
  );
}
